/**
 * Group Leader Store (Zustand)
 * 
 * Maps group_leader rider_ids to group names for ⭐ display across the SPA.
 * Fetches groups via restClient.getGroups() and builds a lookup map.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { restClient } from '../api/rest-client';

interface GroupLeaderState {
  /** Map of rider_id → group_name for all group leaders */
  groupLeaderMap: Record<string, string>;
  loaded: boolean;

  /** Fetch groups and rebuild the leader map */
  fetchGroupLeaders: () => Promise<void>;
  /** Check if a rider_id is a group leader */
  isGroupLeader: (riderId: string) => boolean;
  /** Get the group name for a group leader, or undefined */
  getLeaderGroupName: (riderId: string) => string | undefined;
}

export const useGroupLeaderStore = create<GroupLeaderState>()(
  devtools(
    (set, get) => ({
      groupLeaderMap: {},
      loaded: false,

      fetchGroupLeaders: async () => {
        try {
          const groups = await restClient.getGroups();
          const map: Record<string, string> = {};
          for (const group of groups) {
            if (group.group_leader) {
              map[group.group_leader] = group.name;
            }
          }
          set({ groupLeaderMap: map, loaded: true });
        } catch (err) {
          console.error('[GroupLeaderStore] Failed to fetch group leaders:', err);
        }
      },

      isGroupLeader: (riderId: string) => {
        return !!riderId && Object.prototype.hasOwnProperty.call(get().groupLeaderMap, riderId);
      },

      getLeaderGroupName: (riderId: string) => {
        return get().groupLeaderMap[riderId];
      },
    }),
    { name: 'GroupLeaderStore' }
  )
);

export default useGroupLeaderStore;
